import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useData } from '../context/DataContext';
import { StatCard } from '../components/ui/StatCard';
import { Card, CardHeader } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Table } from '../components/ui/Table';
import { callBillingAPI } from '../utils/api';
import { formatCurrency } from '../utils/formatters';
import { DollarSignIcon, FileTextIcon, RepeatIcon, AlertTriangleIcon, PlusIcon, UsersIcon, RefreshCwIcon, TrendingUpIcon, CalendarIcon, UserIcon, MailIcon } from 'lucide-react';
// API types
interface ApiInvoice {
  id: string;
  number: string;
  status: string;
  customer_name: string;
  customer_organization?: string;
  source?: string;
  amount: number;
  invoice_date?: number;
}
interface ApiSubscription {
  id: string;
  number: string;
  name: string;
  status: string;
  customer?: {
    name?: string;
    organization?: string;
  };
  cycle: string;
  cycle_num?: number;
  amount: number;
  next_invoice_date: number;
  installments?: number | string;
  passed_installments?: number | string;
}
interface ApiCustomer {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  organization?: string;
}
// Display types
interface DashboardInvoice {
  id: string;
  number: string;
  status: string;
  customer_name: string;
  customer_organization?: string;
  source?: string;
  amount: number;
  invoice_date: Date | null;
}
interface DashboardSubscription {
  id: string;
  number: string;
  name: string;
  status: string;
  customer_name: string;
  customer_organization?: string;
  cycle: string;
  cycle_num: number;
  amount: number;
  next_invoice_date: Date;
}
interface RecentCustomer {
  id: string;
  name: string;
  email: string;
  organization?: string;
}
// Helper: format date as mm-dd-yyyy for API
const formatDateForAPI = (d: Date): string => {
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const year = d.getFullYear();
  return `${month}-${day}-${year}`;
};
// Helper: sum paid invoice amounts across all pages
const fetchPaidInvoiceTotal = async (companyId: string, fromDate: string, toDate: string): Promise<number> => {
  let total = 0;
  let page = 1;
  let hasMore = true;
  while (hasMore) {
    const response = await callBillingAPI('getInvoices', {
      company_id: companyId,
      page,
      status: 'paid',
      from_date: fromDate,
      to_date: toDate
    });
    const items = response.items || [];
    for (const inv of items) {
      total += parseFloat(inv.amount) || 0;
    }
    hasMore = response.has_next_page || false;
    page++;
  }
  return total;
};
// Get status badge for invoices
const getInvoiceStatusBadge = (status: string) => {
  const config: Record<string, {
    variant: 'success' | 'warning' | 'danger' | 'info' | 'default';
    label: string;
  }> = {
    draft: {
      variant: 'default',
      label: 'Draft'
    },
    sent: {
      variant: 'info',
      label: 'Sent'
    },
    paid: {
      variant: 'success',
      label: 'Paid'
    },
    partial_paid: {
      variant: 'warning',
      label: 'Partial'
    },
    overdue: {
      variant: 'danger',
      label: 'Overdue'
    },
    voided: {
      variant: 'default',
      label: 'Voided'
    }
  };
  const c = config[status] || {
    variant: 'default' as const,
    label: status
  };
  return <Badge variant={c.variant} data-id="element-1120">{c.label}</Badge>;
};
// Get cycle display name
const getCycleName = (cycle: string, cycleNum: number = 1): string => {
  const names: Record<string, {
    singular: string;
    plural: string;
  }> = {
    week: {
      singular: 'Week',
      plural: 'Weeks'
    },
    month: {
      singular: 'Month',
      plural: 'Months'
    },
    year: {
      singular: 'Year',
      plural: 'Years'
    }
  };
  const n = names[cycle] || {
    singular: cycle,
    plural: cycle
  };
  return cycleNum === 1 ? `Every ${n.singular}` : `Every ${cycleNum} ${n.plural}`;
};
// Format date
const formatDate = (date: Date): string => {
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};
export function Dashboard() {
  const {
    selectedCompanyId,
    selectedCompany
  } = useData();
  const navigate = useNavigate();
  // Data state
  const [recentInvoices, setRecentInvoices] = useState<DashboardInvoice[]>([]);
  const [upcomingSubscriptions, setUpcomingSubscriptions] = useState<DashboardSubscription[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  // Stats
  const [totalCustomers, setTotalCustomers] = useState(0);
  const [unpaidCount, setUnpaidCount] = useState(0);
  const [overdueCount, setOverdueCount] = useState(0);
  const [activeSubsCount, setActiveSubsCount] = useState(0);
  const [salesMTD, setSalesMTD] = useState(0);
  const [salesYTD, setSalesYTD] = useState(0);
  const [expectedMonthRevenue, setExpectedMonthRevenue] = useState<number | null>(null);
  const [recentCustomers, setRecentCustomers] = useState<RecentCustomer[]>([]);
  // Fetch dashboard data
  const fetchDashboardData = async () => {
    if (!selectedCompanyId) return;
    setIsLoading(true);
    try {
      // Date ranges for MTD and YTD
      const now = new Date();
      const mtdFrom = new Date(now.getFullYear(), now.getMonth(), 1);
      const ytdFrom = new Date(now.getFullYear(), 0, 1);
      // Fetch all data in parallel
      const [invoicesRes, sentRes, overdueRes, subsRes, customersRes, mtdAgg, ytdAgg] = await Promise.all([callBillingAPI('getInvoices', {
        company_id: selectedCompanyId,
        page: 1,
        per_page_limit: 8
      }), callBillingAPI('getInvoices', {
        company_id: selectedCompanyId,
        page: 1,
        status: 'sent',
        per_page_limit: 1
      }), callBillingAPI('getInvoices', {
        company_id: selectedCompanyId,
        page: 1,
        status: 'overdue',
        per_page_limit: 1
      }), callBillingAPI('getSubscriptions', {
        company_id: selectedCompanyId,
        page: 1,
        status: 'live',
        per_page_limit: 5
      }), callBillingAPI('getCustomers', {
        company_id: selectedCompanyId,
        page: 1,
        per_page_limit: 10,
        recent_order: 1
      }), callBillingAPI('getInvoicesAggregation', {
        company_id: selectedCompanyId,
        from_date: formatDateForAPI(mtdFrom),
        to_date: formatDateForAPI(now)
      }), callBillingAPI('getInvoicesAggregation', {
        company_id: selectedCompanyId,
        from_date: formatDateForAPI(ytdFrom),
        to_date: formatDateForAPI(now)
      })]);
      // Recent invoices
      const apiInvoices: ApiInvoice[] = invoicesRes.items || [];
      setRecentInvoices(apiInvoices.slice(0, 8).map(inv => ({
        id: inv.id,
        number: inv.number,
        status: inv.status,
        customer_name: inv.customer_name,
        customer_organization: inv.customer_organization || '',
        source: inv.source || '',
        amount: inv.amount,
        invoice_date: inv.invoice_date ? new Date(inv.invoice_date * 1000) : null
      })));
      // Stats
      setUnpaidCount(sentRes.total_count || 0);
      setOverdueCount(overdueRes.total_count || 0);
      setActiveSubsCount(subsRes.total_count || 0);
      setTotalCustomers(customersRes.total_count || 0);
      // Recent customers (last 10)
      const apiCustomers: ApiCustomer[] = customersRes.items || [];
      setRecentCustomers(apiCustomers.slice(0, 10).map(c => ({
        id: c.id,
        name: `${c.first_name} ${c.last_name}`.trim() || c.email,
        email: c.email || '',
        organization: c.organization && c.organization.trim() !== `${c.first_name} ${c.last_name}`.trim() ? c.organization : ''
      })));
      // Upcoming subscriptions (live, sorted by next_invoice_date)
      const apiSubs: ApiSubscription[] = subsRes.items || [];
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const transformed: DashboardSubscription[] = apiSubs.map(sub => ({
        id: sub.id,
        number: sub.number,
        name: sub.name,
        status: sub.status,
        customer_name: sub.customer?.name || '',
        customer_organization: sub.customer?.organization || '',
        cycle: sub.cycle,
        cycle_num: parseInt(String(sub.cycle_num)) || 1,
        amount: sub.amount,
        next_invoice_date: new Date(sub.next_invoice_date * 1000)
      })).filter(sub => {
        const inst = apiSubs.find(s => s.id === sub.id);
        if (!inst) return true;
        const installments = parseInt(String(inst.installments)) || 0;
        const passed = parseInt(String(inst.passed_installments)) || 0;
        return installments === 0 || passed < installments;
      }).sort((a, b) => a.next_invoice_date.getTime() - b.next_invoice_date.getTime()).slice(0, 5);
      setUpcomingSubscriptions(transformed);
      // Set MTD and YTD from aggregation results
      setSalesMTD(parseFloat(mtdAgg.total_amount) || 0);
      setSalesYTD(parseFloat(ytdAgg.total_amount) || 0);
      // Fetch expected month revenue from subscriptions
      try {
        const revenueRes = await callBillingAPI('getSubscriptionsMonthRevenue', {
          company_id: selectedCompanyId
        });
        setExpectedMonthRevenue(parseFloat(revenueRes.amount ?? revenueRes.total ?? revenueRes) || 0);
      } catch (e) {
        console.error('Failed to fetch expected month revenue:', e);
        setExpectedMonthRevenue(null);
      }
    } catch (err) {
      console.error('Failed to fetch dashboard data:', err);
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    fetchDashboardData();
  }, [selectedCompanyId]);
  // Click handlers for stat cards
  const handleUnpaidClick = () => {
    navigate('/invoices?status=sent');
  };
  const handleOverdueClick = () => {
    navigate('/invoices?status=overdue');
  };
  const handleActiveSubscriptionsClick = () => {
    navigate('/subscriptions', {
      state: {
        filterStatus: 'live'
      }
    });
  };
  const handleCustomersClick = () => {
    navigate('/customers');
  };
  const invoiceColumns = [{
    key: 'number',
    header: 'Invoice',
    render: (invoice: DashboardInvoice) => {
      const isEditable = invoice.status === 'draft';
      return <div data-id="element-1121">
            <Link to={isEditable ? `/invoices/${invoice.id}/edit` : `/invoices/${invoice.id}`} className="text-blue-600 hover:text-blue-800 font-medium" data-id="element-1122">
              {invoice.number || invoice.id.slice(0, 8)}
            </Link>
            {invoice.source && <p className="text-xs text-gray-500" data-id="element-1123">{invoice.source}</p>}
          </div>;
    }
  }, {
    key: 'customer',
    header: 'Customer',
    render: (invoice: DashboardInvoice) => {
      const showOrg = invoice.customer_organization && invoice.customer_organization.trim() !== (invoice.customer_name || '').trim();
      return <div data-id="element-1124">
            <span className="font-medium" data-id="element-1125">
              {invoice.customer_name || 'Unknown'}
            </span>
            {showOrg && <p className="text-xs text-gray-500" data-id="element-1126">
                {invoice.customer_organization}
              </p>}
          </div>;
    }
  }, {
    key: 'date',
    header: 'Date',
    render: (invoice: DashboardInvoice) => <span className={invoice.invoice_date ? '' : 'text-gray-400'} data-id="element-1127">
          {invoice.invoice_date ? formatDate(invoice.invoice_date) : '—'}
        </span>
  }, {
    key: 'amount',
    header: 'Amount',
    render: (invoice: DashboardInvoice) => <span className="font-semibold" data-id="element-1128">{formatCurrency(invoice.amount)}</span>
  }, {
    key: 'status',
    header: 'Status',
    render: (invoice: DashboardInvoice) => getInvoiceStatusBadge(invoice.status)
  }];
  return <div className="space-y-8" data-id="element-1129">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-1130">
        <div data-id="element-1131">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-1132">Dashboard</h1>
          <p className="text-gray-500 mt-1" data-id="element-1133">
            Welcome back! Here's what's happening with{' '}
            {selectedCompany?.name || 'your company'}.
          </p>
        </div>
        <div className="flex items-center gap-3" data-id="element-1134">
          <Button variant="secondary" onClick={fetchDashboardData} disabled={isLoading} leftIcon={<RefreshCwIcon className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} data-id="element-1136" />} data-id="element-1135">
            Refresh
          </Button>
          <Link to="/invoices/new" data-id="element-1137">
            <Button leftIcon={<PlusIcon className="w-4 h-4" data-id="element-1139" />} data-id="element-1138">
              New Invoice
            </Button>
          </Link>
          <Link to="/subscriptions/new" data-id="element-1140">
            <Button variant="secondary" leftIcon={<PlusIcon className="w-4 h-4" data-id="element-1142" />} data-id="element-1141">
              New Subscription
            </Button>
          </Link>
        </div>
      </div>

      {/* Expected Month Revenue */}
      {expectedMonthRevenue !== null && <div className="rounded-xl border border-indigo-200 bg-indigo-50 p-5 flex items-center gap-4" data-id="element-1143">
          <div className="w-12 h-12 rounded-lg bg-indigo-100 flex items-center justify-center" data-id="element-1144">
            <DollarSignIcon className="w-6 h-6 text-indigo-600" data-id="element-1145" />
          </div>
          <div data-id="element-1146">
            <p className="text-sm font-medium text-indigo-600" data-id="element-1147">
              Expected Monthly Recurring Revenue
            </p>
            <p className="text-2xl font-bold text-gray-900" data-id="element-1148">
              {isLoading ? '—' : formatCurrency(expectedMonthRevenue)}
            </p>
          </div>
        </div>}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-1149">
        <StatCard title="Sales MTD" value={isLoading ? '—' : formatCurrency(salesMTD)} icon={<CalendarIcon className="w-6 h-6" data-id="element-1151" />} className="border-green-200 bg-green-50" data-id="element-1150" />
        <StatCard title="Sales YTD" value={isLoading ? '—' : formatCurrency(salesYTD)} icon={<TrendingUpIcon className="w-6 h-6" data-id="element-1153" />} className="border-blue-200 bg-blue-50" data-id="element-1152" />
        <StatCard title="Total Customers" value={isLoading ? '—' : totalCustomers} icon={<UsersIcon className="w-6 h-6" data-id="element-1155" />} onClick={handleCustomersClick} className="cursor-pointer hover:shadow-md transition-shadow" data-id="element-1154" />
        <StatCard title="Unpaid Invoices" value={isLoading ? '—' : unpaidCount} icon={<FileTextIcon className="w-6 h-6" data-id="element-1157" />} onClick={handleUnpaidClick} className="cursor-pointer hover:shadow-md transition-shadow" data-id="element-1156" />
        <StatCard title="Active Subscriptions" value={isLoading ? '—' : activeSubsCount} icon={<RepeatIcon className="w-6 h-6" data-id="element-1159" />} onClick={handleActiveSubscriptionsClick} className="cursor-pointer hover:shadow-md transition-shadow" data-id="element-1158" />
        <StatCard title="Overdue Invoices" value={isLoading ? '—' : overdueCount} icon={<AlertTriangleIcon className="w-6 h-6" data-id="element-1161" />} onClick={handleOverdueClick} className={`cursor-pointer hover:shadow-md transition-shadow ${overdueCount > 0 ? 'border-red-200 bg-red-50' : ''}`} data-id="element-1160" />
      </div>

      {/* Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-1162">
        {/* Recent Invoices */}
        <div className="lg:col-span-2" data-id="element-1163">
          <Card padding="none" data-id="element-1164">
            <div className="p-6 border-b border-gray-200" data-id="element-1165">
              <CardHeader title="Recent Invoices" subtitle="Your latest billing activity" action={<Link to="/invoices" data-id="element-1167">
                    <Button variant="ghost" size="sm" data-id="element-1168">
                      View All
                    </Button>
                  </Link>} data-id="element-1166" />
            </div>
            {isLoading ? <div className="p-12 text-center" data-id="element-1169">
                <div className="inline-block w-6 h-6 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" data-id="element-1170"></div>
                <p className="mt-3 text-sm text-gray-500" data-id="element-1171">
                  Loading invoices...
                </p>
              </div> : <Table columns={invoiceColumns} data={recentInvoices} keyExtractor={inv => inv.id} emptyMessage="No invoices yet" data-id="element-1172" />}
          </Card>
        </div>

        {/* Upcoming Subscriptions */}
        <div data-id="element-1173">
          <Card padding="none" data-id="element-1174">
            <div className="p-6 border-b border-gray-200" data-id="element-1175">
              <CardHeader title="Upcoming Renewals" subtitle="Next billing dates" action={<Link to="/subscriptions" data-id="element-1177">
                    <Button variant="ghost" size="sm" data-id="element-1178">
                      View All
                    </Button>
                  </Link>} data-id="element-1176" />
            </div>
            {isLoading ? <div className="p-12 text-center" data-id="element-1179">
                <div className="inline-block w-6 h-6 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" data-id="element-1180"></div>
                <p className="mt-3 text-sm text-gray-500" data-id="element-1181">Loading...</p>
              </div> : <div className="divide-y divide-gray-200" data-id="element-1182">
                {upcomingSubscriptions.length === 0 ? <div className="p-6 text-center text-gray-500" data-id="element-1183">
                    No upcoming renewals
                  </div> : upcomingSubscriptions.map(sub => {
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              const invoiceDate = new Date(sub.next_invoice_date);
              invoiceDate.setHours(0, 0, 0, 0);
              const isOverdue = invoiceDate < today;
              return <Link key={sub.id} to={`/subscriptions/${sub.id}`} className="block p-4 hover:bg-gray-50 transition-colors" data-id="element-1184">
                        <div className="flex items-center justify-between" data-id="element-1185">
                          <div data-id="element-1186">
                            <p className="font-medium text-gray-900" data-id="element-1187">
                              {sub.customer_name || 'Unknown'}
                            </p>
                            <p className="text-sm text-gray-500" data-id="element-1188">
                              {sub.name || getCycleName(sub.cycle, sub.cycle_num)}
                            </p>
                          </div>
                          <div className="text-right" data-id="element-1189">
                            <p className={`text-sm font-medium ${isOverdue ? 'text-red-600' : 'text-gray-900'}`} data-id="element-1190">
                              {formatDate(sub.next_invoice_date)}
                            </p>
                            {isOverdue && <p className="text-xs text-red-600" data-id="element-1191">Overdue</p>}
                          </div>
                        </div>
                      </Link>;
            })}
              </div>}
          </Card>

          {/* Recent Customers */}
          <Card padding="none" className="mt-8" data-id="element-1192">
            <div className="p-6 border-b border-gray-200" data-id="element-1193">
              <CardHeader title="Recent Customers" subtitle="Last 10 added or edited" action={<Link to="/customers" data-id="element-1195">
                    <Button variant="ghost" size="sm" data-id="element-1196">
                      View All
                    </Button>
                  </Link>} data-id="element-1194" />
            </div>
            {isLoading ? <div className="p-12 text-center" data-id="element-1197">
                <div className="inline-block w-6 h-6 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" data-id="element-1198"></div>
                <p className="mt-3 text-sm text-gray-500" data-id="element-1199">Loading...</p>
              </div> : <div className="divide-y divide-gray-200" data-id="element-1200">
                {recentCustomers.length === 0 ? <div className="p-6 text-center text-gray-500" data-id="element-1201">
                    No customers yet
                  </div> : recentCustomers.map(customer => <Link key={customer.id} to={`/customers/${customer.id}`} className="p-4 flex items-center gap-3 hover:bg-gray-50 transition-colors block" data-id="element-1202">
                      <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0" data-id="element-1203">
                        <UserIcon className="w-4 h-4 text-gray-400" data-id="element-1204" />
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-1205">
                        <p className="font-medium text-blue-600 text-sm truncate" data-id="element-1206">
                          {customer.name}
                        </p>
                        {customer.organization && <p className="text-xs text-gray-400 truncate" data-id="element-1207">
                            {customer.organization}
                          </p>}
                        {customer.email && <p className="text-xs text-gray-500 truncate flex items-center gap-1" data-id="element-1208">
                            <MailIcon className="w-3 h-3 text-gray-400" data-id="element-1209" />
                            {customer.email}
                          </p>}
                      </div>
                    </Link>)}
              </div>}
          </Card>
        </div>
      </div>
    </div>;
}